/**
 * step.facade.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Observable, Subscription } from "rxjs/Rx";

import { MessageReceiver } from "../../server/message-receiver";
import { MessagingService } from "../../server/messaging.service";
import { disconnectWith } from "../../support/types/decorators";
import { AlgorithmStep, AlgorithmStepData } from "../structure/algorithm-step";
import { AlgorithmService } from "./algorithm.service";

/**
 * A {@link StepFacade} is a generic interface for services that provide means
 * to access and track particular step types, and their interactions with the
 * server.
 */
export abstract class StepFacade<S extends AlgorithmStep>
  implements MessageReceiver
{
  public constructor (
    protected messaging : MessagingService,
    protected algorithms : AlgorithmService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected());
  }

  @disconnectWith("unsubscribe")
  protected subscriptions : Subscription = new Subscription();

  public abstract connected () : void;
  public disconnected () : void {/* decorator generated */}
  protected abstract matchesSubtype (step : AlgorithmStep) : step is S;

  protected isRunning () : boolean
  {
    return this.algorithms.isRunning;
  }
  protected assertRunning () : void
  {
    this.algorithms.assertRunning();
  }

  public get (id : number) : S
  {
    if (!this.isRunning())
    {
      return undefined;
    }
    const step : AlgorithmStep = this.algorithms.running.steps.get(id);
    return (this.matchesSubtype(step)) ? step : undefined;
  }

  // TODO: maybe memoize this?
  public findNamed (name : string) : S | null
  {
    if (!this.isRunning())
    {
      return undefined;
    }
    const steps : AlgorithmStep[] = this.algorithms.running.steps.filtered(
      (compare : AlgorithmStep) => compare.name === name);
    if (steps.length !== 1)
    {
      return null;
    }
    const step : AlgorithmStep = steps[0];
    return (this.matchesSubtype(step)) ? step : undefined;
  }

  public has (id : number) : boolean
  {
    if (!this.isRunning())
    {
      return false;
    }
    return this.matchesSubtype(this.algorithms.running.steps.get(id));
  }

  public abstract all () : S[];

  public updatingSet () : Observable<S[]>
  {
    return this.algorithms.running.steps.updates.map(
      (u : any) => this.all());
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/step.facade.ts